import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import Breadcrumb from "../../../components/layout/Breadcrumb";
import { fetchPolicies, updatePolicy } from "../../../redux/slice/policiesSlice";
import { useLocation, useNavigate } from "react-router-dom";
import { Button, Form } from "react-bootstrap";
import { toast } from "react-toastify";
import { Editor } from '@tinymce/tinymce-react';
import { useHasPermission } from "../../../hooks/useHasPermission";

export default function PolicyDetail() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const query = new URLSearchParams(useLocation().search);
  const id = Number(query.get("id"));
  const { list, loading } = useSelector((state) => state.policies);
  const policy = list.find((p) => p.id === id);
  const [title, setTitle] = useState(policy?.title || "");
  const [contentHtml] = useState(policy?.contentHtml || "");
  const [isActive, setIsActive] = useState(policy?.status ?? true);
  const editorRef = useRef(null);
  const canEditPolicy = useHasPermission("Policies-manage");

  useEffect(() => {
    if (list.length === 0) {
      dispatch(fetchPolicies());
    }
  }, [dispatch, list.length]);

  const handleUpdate = async (e) => {
    e.preventDefault();
    const editorContent = editorRef.current ? editorRef.current.getContent() : contentHtml;
    const payload = {
      title,
      contentHtml: editorContent,
      isActive,
    };
    try {
      await dispatch(updatePolicy({ id, payload })).unwrap();
      toast.success("Policy updated successfully", { className: 'toast-success-custom' });
      navigate("/admin/policies");
    } catch (error) {
      toast.error(error?.message || "Update failed", { className: 'toast-error-custom', autoClose: 4000 });
    }
  };

  const tinyConfig = {
    height: 500,
    menubar: "file edit view insert format tools table image help",
    branding: false,
    promotion: false,
    statusbar: true,
    resize: true,

    plugins: [
      "advlist", "autolink", "lists", "link", "image", "charmap", "preview", "anchor",
      "searchreplace", "visualblocks", "code", "fullscreen", "insertdatetime",
      "media", "table", "help", "wordcount"
    ],

    toolbar:
      "undo redo | blocks fontfamily fontsize | bold italic underline strikethrough | " +
      "image | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | " +
      "link media table | searchreplace preview fullscreen code | help",

    content_style:
      "body { font-family: Helvetica, Arial, sans-serif; font-size:14px }",
    image_title: false,
    image_dimensions: false,
    image_advtab: false,
    file_picker_types: "image",

    file_picker_callback: (callback, value, meta) => {
      if (meta.filetype !== "image") return;
      const input = document.createElement("input");
      input.type = "file";
      input.accept = "image/*";
      input.onchange = () => {
        const file = input.files[0];
        const reader = new FileReader();
        reader.onload = () => {
          callback(reader.result, { alt: file.name });
        };
        reader.readAsDataURL(file);
      };
      input.click();
    },

    images_upload_handler: (blobInfo) =>
      new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.readAsDataURL(blobInfo.blob());
      }),
  };

  if (!canEditPolicy) {
    return (<p className="perm-msg">You don’t have permission to view policy details!</p>);
  }
  if (loading) return <p>Loading policy...</p>;
  if (!policy && !loading) {
    return <p>Policy not found</p>;
  }

  return (
    <div className="section-body policy-update">
      <div className="container-fluid">

        {/* 🔹 Custom breadcrumb for this page */}
        <Breadcrumb
          overrideTrail={[
            { name: "Dashboard", path: "/admin/dashboard" },
            { name: "Policies", path: "/admin/policies" }, // 🔹 updated
            { name: `Edit (${policy.title})` } // 🔹 dynamic title, no path → active
          ]}
        />
        <div className="d-flex justify-content-between mt-3 mb-4 align-middle">
          <h5 className="mb-3 mt-1 d-none-m">Edit Policy</h5>

          <div className="d-flex align-items-end">
            <div className="policy-status-toggle">
              <input
                type="radio"
                id="publish"
                name="policyStatus"
                checked={isActive === true}
                onChange={() => setIsActive(true)}
              />
              <label htmlFor="publish" className="publish m-0">Publish</label>

              <input
                type="radio"
                id="draft"
                name="policyStatus"
                checked={isActive === false}
                onChange={() => setIsActive(false)}
              />
              <label htmlFor="draft" className="draft m-0">Draft</label>
            </div>

            <Button type="submit" form="policyForm" className="primary-btn ml-4">
              Update Policy
            </Button>
          </div>
        </div>

        <div className="card mb-5">
          <div className="card-header">
            <Form id="policyForm" onSubmit={handleUpdate}>
              <Form.Group className="mb-3">
                <Form.Label><strong>Policy Title:</strong></Form.Label>
                <Form.Control
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label><strong>Policy Content: </strong></Form.Label>
                <Editor
                  onInit={(evt, editor) => (editorRef.current = editor)}
                  initialValue={contentHtml}
                  init={tinyConfig}
                  apiKey="x1zlwv4dd93x8wuuh7iovblba5p75zd4iwk50u953zh33s2p"
                />
              </Form.Group>
            </Form>
          </div>
        </div>
      </div>
    </div>
  );
}
